import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { UIUtils } from "@/shared/utils/UIUtils";

const MULTI_PLAN_GUIDE_EDIT_THRESHOLD = 15;
const MULTI_PLAN_GUIDE_COOLDOWN_MS = 1000;
const MULTI_PLAN_GUIDE_SKIP_DELAY_PARAM = "skipDelay";

/**
 * D1/D2: edit counter + nudge trigger.
 *
 * Counts coverage-option edits (at the handler level — quote mutations are
 * throttled/serialized, so network-level counting undercounts). Once the
 * threshold is reached, waits for any in-flight price recalculation to clear
 * plus a 1s cooldown, then flips `isNudgeVisible`. The caller ANDs visibility
 * with the experiment gate (the counter must run in both arms because the
 * threshold also gates the Statsig exposure). Dismissing or accepting is
 * terminal for the session: `multiPlanGuideDone` lives in app state so it
 * survives step navigation; a page refresh resets everything.
 */
export const useMultiPlanGuideNudge = () => {
    const { appState, updateAppState } = useAppLayerContext();
    const { isQuoteUpdating, multiPlanGuideDone } = appState;
    const searchParams = useSearchParams();

    const [editCount, setEditCount] = useState(0);
    const [isNudgeVisible, setIsNudgeVisible] = useState(false);

    // Dev/QA escape hatch (same non-prod URL-param pattern as `enableCheckout`):
    // `&skipDelay` arms the nudge immediately — no 15-edit threshold, no 1s cooldown —
    // so QA/Playwright can reach the variant UI without 15 quote PUTs. Never honored in
    // production, and the experiment gate still applies: the toast only shows when the
    // variant arm is active (e.g. `?tuid=enable-multi-plan-guide&skipDelay`).
    const skipDelay = useMemo(() => UIUtils.isNonProdQueryFlagEnabled(new URLSearchParams(searchParams.toString()), MULTI_PLAN_GUIDE_SKIP_DELAY_PARAM), [searchParams]);

    const registerEdit = useCallback(() => {
        setEditCount(count => Math.min(count + 1, MULTI_PLAN_GUIDE_EDIT_THRESHOLD));
    }, []);

    const hasReachedEditThreshold = skipDelay || editCount >= MULTI_PLAN_GUIDE_EDIT_THRESHOLD;

    // The effect is the state machine: while the recalculation is in flight nothing is
    // scheduled; when it clears, the 1s cooldown starts. A new mutation during the
    // cooldown re-runs the effect, and the cleanup cancels the pending timer (re-arm).
    useEffect(() => {
        if (multiPlanGuideDone || isNudgeVisible || !hasReachedEditThreshold || isQuoteUpdating) return;

        const timer = setTimeout(() => setIsNudgeVisible(true), skipDelay ? 0 : MULTI_PLAN_GUIDE_COOLDOWN_MS);
        return () => clearTimeout(timer);
    }, [multiPlanGuideDone, isNudgeVisible, hasReachedEditThreshold, isQuoteUpdating, skipDelay]);

    // D2: both paths are terminal for the session
    const dismissNudge = useCallback(() => {
        setIsNudgeVisible(false);
        updateAppState({ multiPlanGuideDone: true });
    }, [updateAppState]);

    return { registerEdit, hasReachedEditThreshold, isNudgeVisible, dismissNudge };
};
